import { MODE_EMPTY_ROW, MODE_FIELDS, ROLE_LIMITS, WEAPON_ROLE_OPTIONS } from './constants';

const roleLabelMap = WEAPON_ROLE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const copyRoleLabelMap = {
  AR: 'Assault Rifle',
  SMG: 'Sub-Machine Gun',
  LMG: 'Light-Machine Gun',
  SHOTGUN: 'Shotgun',
  MARKSMAN: 'Marksman',
  SNIPER: 'Sniper',
};

export const createEmptyRow = (mode, player = '') => ({
  ...MODE_EMPTY_ROW[mode],
  player,
});

export const buildModeRowsFromRoster = (mode, players = [], existingRows = []) => {
  const sourceRows = Array.isArray(existingRows) ? existingRows : [];
  const seenPlayers = new Set();

  // Preserve the current/saved slot order first so S1/S2/etc. stay stable.
  const orderedRows = sourceRows.map((row) => {
    const normalized = { ...createEmptyRow(mode), ...row };
    if (normalized.player) {
      seenPlayers.add(normalized.player);
    }
    return normalized;
  });

  // Only append active roster players that don't already occupy a slot.
  const appendedRosterRows = (players || [])
    .filter((player) => player && !seenPlayers.has(player))
    .map((player) => createEmptyRow(mode, player));

  const merged = [...orderedRows, ...appendedRosterRows];
  return merged.length ? merged : [createEmptyRow(mode)];
};

export const sanitizeRowsForSave = (mode, rows = []) => {
  const allowedFields = MODE_FIELDS[mode] || [];

  return rows
    .map((row) => {
      const next = {};
      allowedFields.forEach((field) => {
        next[field] = String(row?.[field] || '').trim();
      });
      return next;
    })
    .filter((row) => Object.values(row).some(Boolean));
};

export const getRoleUsage = (rows = []) =>
  rows.reduce((acc, row) => {
    [row?.primaryRole, row?.secondaryRole].forEach((role) => {
      if (!role) return;
      acc[role] = (acc[role] || 0) + 1;
    });
    return acc;
  }, {});

export const validateAssignments = (rows = []) => {
  const errors = [];
  const seenPlayers = new Set();
  const assignedRows = rows.filter((row) => row?.player);
  const starterCount = assignedRows.filter((row) => (row?.status || 'STARTER') === 'STARTER').length;
  const subCount = assignedRows.filter((row) => row?.status === 'SUB').length;

  rows.forEach((row, index) => {
    const label = row?.player || `Row ${index + 1}`;
    if (row?.player) {
      if (seenPlayers.has(row.player)) {
        errors.push(`${label} is assigned more than once.`);
      }
      seenPlayers.add(row.player);
    }

    if (row?.primaryRole && row?.primaryRole === row?.secondaryRole) {
      errors.push(`${label} cannot use the same weapon class role twice.`);
    }
  });

  if (starterCount > 5) {
    errors.push(`Only 5 players can be marked as starters at one time (${starterCount}/5).`);
  }

  if (assignedRows.length >= 5 && starterCount < 5) {
    errors.push(`Set 5 players as starters before finalizing the assignment.`);
  }

  if (assignedRows.length > 5 && subCount < 1) {
    errors.push(`Mark at least 1 player as sub when more than 5 players are assigned.`);
  }

  const usage = getRoleUsage(rows);
  Object.entries(ROLE_LIMITS).forEach(([role, limit]) => {
    if ((usage[role] || 0) > limit) {
      errors.push(`${roleLabelMap[role]} exceed the pool limit (${usage[role]}/${limit}).`);
    }
  });

  return {
    errors,
    roleUsage: usage,
    starterCount,
    subCount,
    assignedCount: assignedRows.length,
    isValid: errors.length === 0,
  };
};

export const getRoleStatusTone = (role, count) => {
  const limit = ROLE_LIMITS[role] || 0;
  if (count > limit) return 'invalid';
  if (count === limit) return 'full';
  return 'available';
};

export const buildPlayingFiveCopy = (mode, rows = []) => {
  const starters = rows
    .filter((row) => row?.player && (row?.status || 'STARTER') === 'STARTER')
    .slice(0, 5);

  return starters
    .map((row) => {
      const primary = copyRoleLabelMap[row.primaryRole] || row.primaryRole || '-';
      const secondary = copyRoleLabelMap[row.secondaryRole] || row.secondaryRole || '-';
      const base = `${row.player} - ${primary} / ${secondary}`;

      if (mode === 'SND') {
        return base;
      }

      return `${base} | ${row.operator || '-'}`;
    })
    .join('\n');
};
